import React, { useState, useEffect, useRef } from 'react';
import { Box, Drawer, List, ListItem, ListItemText, ListItemIcon, IconButton, Typography, useTheme, useMediaQuery, Divider, TextField, Paper } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import ChatIcon from '@mui/icons-material/Chat';
import SettingsIcon from '@mui/icons-material/Settings';
import SendIcon from '@mui/icons-material/Send';
import Message from './Message';
import PaymentModal from './PaymentModal';

const drawerWidth = 260;

const ChatLayout = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const [messages, setMessages] = useState([
        { id: 1, text: "Hello! I'm Catchinsta AI. I can help you find products from Amazon and other sites. Try saying 'buy me a laptop'.", sender: 'bot', type: 'text' }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [paymentOpen, setPaymentOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleBuyClick = (product) => {
        setSelectedProduct(product);
        setPaymentOpen(true);
    };

    const handlePaymentConfirm = () => {
        setPaymentOpen(false);
        setMessages(prev => [...prev, {
            id: Date.now(),
            text: `Payment successful for ${selectedProduct.name}! Your order has been placed.`,
            sender: 'bot',
            type: 'text'
        }]);
    };

    const simulateBotResponse = (userText) => {
        setIsTyping(true);
        setTimeout(() => {
            let newMsg = {
                id: Date.now(),
                sender: 'bot',
                type: 'text',
                text: "I'm not sure how to help with that yet. Try asking me to buy something!"
            };

            if (userText.toLowerCase().includes('buy')) {
                // Mock product search logic
                const product = {
                    name: "MacBook Pro M3",
                    description: "Apple 2024 MacBook Pro Laptop M3 Pro chip with 12‑core CPU, 18‑core GPU: 14.2-inch Liquid Retina XDR Display, 18GB Unified Memory, 512GB SSD Storage.",
                    price: 1999,
                    image: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?auto=format&fit=crop&q=80&w=1000"
                };

                if (userText.toLowerCase().includes('iphone')) {
                    product.name = "iPhone 15 Pro Max";
                    product.description = "Titanium design, A17 Pro chip, 48MP Main camera, USB-C.";
                    product.price = 1199;
                    product.image = "https://images.unsplash.com/photo-1696446701796-da61225697cc?auto=format&fit=crop&q=80&w=1000";
                }

                if (userText.toLowerCase().includes('shoes')) {
                    product.name = "Nike Air Max";
                    product.description = "Comfortable and stylish running shoes.";
                    product.price = 129;
                    product.image = "https://images.unsplash.com/photo-1542291026-7eec264c27ff?auto=format&fit=crop&q=80&w=1000";
                }

                newMsg = {
                    id: Date.now(),
                    sender: 'bot',
                    type: 'product-card',
                    text: `I found this ${product.name} for you. Would you like to buy it?`,
                    product: product
                };
            }

            setMessages(prev => [...prev, newMsg]);
            setIsTyping(false);
        }, 1500);
    };

    const handleSend = () => {
        if (!inputValue.trim()) return;

        const userMsg = {
            id: Date.now(),
            text: inputValue,
            sender: 'user',
            type: 'text'
        };

        setMessages(prev => [...prev, userMsg]);
        simulateBotResponse(inputValue);
        setInputValue('');
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const drawerContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', background: theme.typography.h1.background, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    Catchinsta
                </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
                <Box
                    sx={{
                        p: 1.5,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        transition: '0.2s',
                        '&:hover': { bgcolor: 'action.hover' }
                    }}
                    onClick={() => setMessages([{ id: 1, text: "Hello! I'm Catchinsta AI. I can help you find products from Amazon and other sites. Try saying 'buy me a laptop'.", sender: 'bot', type: 'text' }])}
                >
                    <AddIcon fontSize="small" />
                    <Typography variant="body2">New chat</Typography>
                </Box>
            </Box>
            <List sx={{ flexGrow: 1, overflowY: 'auto' }}>
                <ListItem button sx={{ borderRadius: 2, mx: 1 }}>
                    <ListItemIcon><ChatIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Previous Chat 1" primaryTypographyProps={{ noWrap: true, fontSize: '0.9rem' }} />
                </ListItem>
                <ListItem button sx={{ borderRadius: 2, mx: 1 }}>
                    <ListItemIcon><ChatIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Shopping List" primaryTypographyProps={{ noWrap: true, fontSize: '0.9rem' }} />
                </ListItem>
            </List>
            <Divider />
            <List>
                <ListItem button sx={{ borderRadius: 2, mx: 1 }}>
                    <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Settings" />
                </ListItem>
            </List>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
            <Box
                component="nav"
                sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
            >
                {isMobile ? (
                    <Drawer
                        variant="temporary"
                        open={mobileOpen}
                        onClose={handleDrawerToggle}
                        ModalProps={{ keepMounted: true }}
                        sx={{
                            display: { xs: 'block', md: 'none' },
                            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                        }}
                    >
                        {drawerContent}
                    </Drawer>
                ) : (
                    <Drawer
                        variant="permanent"
                        sx={{
                            display: { xs: 'none', md: 'block' },
                            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: `1px solid ${theme.palette.divider}` },
                        }}
                        open
                    >
                        {drawerContent}
                    </Drawer>
                )}
            </Box>

            <Box component="main" sx={{ flexGrow: 1, height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                {isMobile && (
                    <Box sx={{ p: 1, display: 'flex', alignItems: 'center', borderBottom: `1px solid ${theme.palette.divider}` }}>
                        <IconButton onClick={handleDrawerToggle} edge="start" sx={{ mr: 2 }}>
                            <MenuIcon />
                        </IconButton>
                        <Typography variant="h6">Catchinsta</Typography>
                    </Box>
                )}

                {/* Message List */}
                <Box sx={{ flexGrow: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                    {messages.length === 0 ? (
                        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', opacity: 0.5 }}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Catchinsta</Typography>
                            <Typography>Your Shopping Assistant</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ pb: 10 }}>
                            {messages.map((msg) => (
                                <Message key={msg.id} message={msg} onBuyClick={handleBuyClick} />
                            ))}
                            {isTyping && (
                                <Box sx={{ p: 3, display: 'flex', gap: 2 }}>
                                    <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>Catchinsta is typing...</Typography>
                                </Box>
                            )}
                            <div ref={messagesEndRef} />
                        </Box>
                    )}
                </Box>

                {/* Input Area */}
                <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}`, bgcolor: 'background.default' }}>
                    <Paper
                        component="form"
                        sx={{ p: '2px 4px', display: 'flex', alignItems: 'center', borderRadius: 4, bgcolor: 'action.hover', boxShadow: 'none' }}
                    >
                        <TextField
                            fullWidth
                            placeholder="Send a message"
                            variant="standard"
                            InputProps={{ disableUnderline: true, sx: { p: 1.5 } }}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={handleKeyPress}
                        />
                        <IconButton color={inputValue.trim() ? "primary" : "default"} sx={{ p: '10px' }} onClick={handleSend} disabled={!inputValue.trim()}>
                            <SendIcon />
                        </IconButton>
                    </Paper>
                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, color: 'text.secondary' }}>
                        Catchinsta can make mistakes. Verify important information.
                    </Typography>
                </Box>
            </Box>

            <PaymentModal
                open={paymentOpen}
                onClose={() => setPaymentOpen(false)}
                product={selectedProduct}
                onConfirm={handlePaymentConfirm}
            />
        </Box>
    );
};

export default ChatLayout;
